import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, X, Check, AlertTriangle, Palette, Edit3, Filter, Loader2 } from "lucide-react";
import { useColors } from "../context/ColorContext"; // ← adjust path as needed

/* ─────────────────────────────────────────────
   HELPERS  –  bridge UI booleans ↔ API strings
   API shape: { id, color_name, code, status }
───────────────────────────────────────────── */
const toActive  = (item) => item.status === "Active";           // API → UI
const toStatus  = (active) => (active ? "Active" : "Inactive"); // UI → API
const getHex    = (item)  => (item.code || "").trim();          // normalise trailing spaces

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success" ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"}`}>
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.type === "error"   && <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-[#e8e0d0] animate-modal-in`}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0ebe0]">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center">
          <Icon size={15} className="text-teal-700" />
        </div>
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      </div>
      <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
        <X size={16} />
      </button>
    </div>
  );
}

function ModalFooter({ onCancel, onConfirm, confirmLabel, loading }) {
  return (
    <div className="flex gap-3 justify-end px-6 py-4 border-t border-[#f0ebe0]">
      <button onClick={onCancel} disabled={loading}
        className="px-5 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-[#f7f3ec] transition-all active:scale-95 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={onConfirm} disabled={loading}
        className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-all active:scale-95 flex items-center gap-2 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed">
        {loading ? <Loader2 size={14} className="animate-spin" /> : confirmLabel}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHARED FIELDS
───────────────────────────────────────────── */
function Field({ label, required, error, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint  && !error && <p className="text-xs text-gray-400">{hint}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function TextInput({ error, ...props }) {
  return (
    <input {...props}
      className={`w-full h-10 px-3 rounded-xl border text-sm bg-[#fdfcfa] text-gray-800 placeholder-gray-400 outline-none transition-all
        ${error ? "border-red-300 ring-2 ring-red-100"
          : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
    />
  );
}

function StatusRow({ active, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-[#fdfcfa]">
      <div>
        <p className="text-sm font-medium text-gray-700">Status</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {active ? "This item is currently active" : "This item is currently inactive"}
        </p>
      </div>
      <button type="button" onClick={onChange}
        className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
          ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
        <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${active ? "translate-x-5" : "translate-x-1"}`} />
      </button>
    </div>
  );
}

function StatusToggle({ active, onChange, disabled }) {
  return (
    <button type="button" onClick={onChange} disabled={disabled}
      className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0
        ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
        ${active ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/* ─────────────────────────────────────────────
   HEX PICKER
───────────────────────────────────────────── */
function HexPicker({ hex, hexInput, onHexChange, onInputChange, error }) {
  const colorRef = useRef(null);
  const isValid  = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);
  return (
    <Field label="Hex Color Code" required error={error} hint="Click the swatch to open color picker">
      <div className="flex gap-2 items-center">
        <button type="button" onClick={() => colorRef.current?.click()}
          className="relative w-10 h-10 rounded-xl border-2 border-[#e2ddd4] hover:border-teal-500 transition-all flex-shrink-0 overflow-hidden cursor-pointer"
          style={{ backgroundColor: isValid(hex) ? hex : "#ccc" }}>
          <input ref={colorRef} type="color" value={isValid(hex) ? hex : "#0D9488"}
            onChange={(e) => onHexChange(e.target.value.toUpperCase())}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
        </button>
        <input type="text" value={hexInput} onChange={(e) => onInputChange(e.target.value)}
          placeholder="#FFFFFF" maxLength={7}
          className={`flex-1 h-10 px-3 rounded-xl border text-sm font-mono bg-[#fdfcfa] text-gray-800 uppercase outline-none transition-all
            ${error ? "border-red-300 ring-2 ring-red-100"
              : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
        />
        {isValid(hex) && (
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center flex-shrink-0">
            <Check size={14} className="text-teal-600" />
          </div>
        )}
      </div>
    </Field>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL  –  calls addColor(data)
───────────────────────────────────────────── */
function AddModal({ open, onClose, onSave }) {
  const [name, setName]         = useState("");
  const [hex, setHex]           = useState("");
  const [hexInput, setHexInput] = useState("");
  const [active, setActive]     = useState(true);
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const isValid = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);

 useEffect(() => {
  if (open) {
    setName("");
    setHex("");
    setHexInput("");
    setActive(true);
    setErrors({});
    setSaving(false);
  }
}, [open]);

const handleHexInput = (raw) => {
  let value = raw.toUpperCase();

  // Always force starting #
  if (!value.startsWith("#")) {
    value = "#" + value.replace(/#/g, "");
  }

  // Allow only valid HEX characters
  value = "#" + value.slice(1).replace(/[^0-9A-F]/g, "");

  // Limit length (# + 6 chars)
  if (value.length > 7) value = value.slice(0, 7);

  setHexInput(value);

  // Validate properly
  if (/^#([0-9A-F]{6})$/.test(value)) {
    setHex(value);
    setErrors((p) => ({ ...p, hex: "" }));
  } else {
    setHex(""); // ❗ IMPORTANT: clear invalid hex
    setErrors((p) => ({ ...p, hex: "Enter valid hex like #FF5733" }));
  }
};

  const handleSave = async () => {
    const e = {};
    if (!name.trim()) e.name = "Colour name is required";
    if (!isValid(hex)) e.hex = "Valid hex color required";
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      // Pass data in the shape the backend expects.
      // Adjust field names (e.g. hex_code vs hex) to match your API contract.
      await onSave({
        color_name: name.trim(),
        code:       hex.toUpperCase(),
        status:     toStatus(active),
      });
      onClose();
    } catch {
      // error toast is shown by the parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Add Colour" icon={Plus} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label="Colour Name" required error={errors.name}>
          <TextInput placeholder="e.g., Crimson Red, Ocean Blue" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
            error={errors.name} />
        </Field>
        <HexPicker hex={hex} hexInput={hexInput} error={errors.hex}
          onHexChange={(v) => { setHex(v); setHexInput(v); setErrors((p) => ({ ...p, hex: "" })); }}
          onInputChange={handleHexInput} />
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave} loading={saving}
        confirmLabel={<><Check size={14} /> Save Colour</>} />    </Modal>
  );
}

/* ─────────────────────────────────────────────
   EDIT MODAL  –  calls updateColor(id, data)
───────────────────────────────────────────── */
function EditModal({ open, onClose, onSave, item }) {
  const [name, setName]         = useState("");
  const [hex, setHex]           = useState("#0D9488");
  const [hexInput, setHexInput] = useState("#0D9488");
  const [active, setActive]     = useState(true);
  const [errors, setErrors]     = useState({});
  const [saving, setSaving]     = useState(false);
  const isValid = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);

  useEffect(() => {
    if (open && item) {
      setName(item.color_name || "");
      const h = getHex(item) || "#0D9488";
      setHex(h); setHexInput(h);
      setActive(toActive(item));
      setErrors({}); setSaving(false);
    }
  }, [open, item]);

  const handleHexInput = (raw) => {
    setHexInput(raw);
    const v = raw.startsWith("#") ? raw : `#${raw}`;
    if (isValid(v)) { setHex(v.toUpperCase()); setErrors((p) => ({ ...p, hex: "" })); }
    else setErrors((p) => ({ ...p, hex: "Enter a valid hex" }));
  };

  const handleSave = async () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (!isValid(hex)) e.hex = "Valid hex required";
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      await onSave(item.id, {
        color_name: name.trim(),
        code:       hex.toUpperCase(),
        status:     toStatus(active),
      });
      onClose();
    } catch {
      // error toast is shown by the parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Edit Colour" icon={Pencil} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label="Colour Name" required error={errors.name}>
          <TextInput placeholder="e.g., Crimson Red, Ocean Blue" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
            error={errors.name} />
        </Field>
        <HexPicker hex={hex} hexInput={hexInput} error={errors.hex}
          onHexChange={(v) => { setHex(v); setHexInput(v); setErrors((p) => ({ ...p, hex: "" })); }}
          onInputChange={handleHexInput} />
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave} loading={saving}
        confirmLabel={<><Check size={14} /> Update Colour</>} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item }) {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm(item?.id);
      onClose();
    } catch {
      // error toast shown by parent
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete Colour?</h3>
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to delete <span className="font-semibold text-gray-700">"{item?.color_name}"</span>? This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={deleting}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60">
            {deleting
              ? <Loader2 size={14} className="animate-spin" />
              : <><Trash2 size={14} /> Delete</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   COLOURS PAGE  –  wired to ColorContext
───────────────────────────────────────────── */
export default function ColoursPage() {
  const {
    colors,
    loading,
    addColor,
    updateColor,
    deleteColor,
    toggleStatus,
  } = useColors();

  const [search, setSearch]     = useState("");
  const [sortBy, setSortBy]     = useState("");
  const [addOpen, setAddOpen]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const [toggling, setToggling] = useState(null); // id currently being toggled
  const { toasts, show: toast } = useToast();
const [filtersOpen, setFiltersOpen] = useState(false);
const [statusFilter, setStatusFilter] = useState("all");
  /* ── derived counts ── */
  const activeCount   = colors.filter((c) => c.status === "Active").length;
  const inactiveCount = colors.length - activeCount;

  /* ── filter ── */
let filtered = colors.filter((c) => {
  const matchSearch =
    (c.color_name || "").toLowerCase().includes(search.toLowerCase()) ||
    getHex(c).toLowerCase().includes(search.toLowerCase());

  const matchStatus =
    statusFilter === "all"
      ? true
      : statusFilter === "active"
      ? c.status === "Active"
      : c.status === "Inactive";

  return matchSearch && matchStatus;
});

  /* ── sort ── */
  if (sortBy === "name_asc")  filtered = [...filtered].sort((a, b) => a.color_name.localeCompare(b.color_name));
  if (sortBy === "name_desc") filtered = [...filtered].sort((a, b) => b.color_name.localeCompare(a.color_name));
  if (sortBy === "active")    filtered = [...filtered].sort((a, b) => (b.status === "Active" ? 1 : 0) - (a.status === "Active" ? 1 : 0));
  if (sortBy === "inactive")  filtered = [...filtered].sort((a, b) => (a.status === "Active" ? 1 : 0) - (b.status === "Active" ? 1 : 0));

  /* ── handlers ── */
  const handleAdd = async (data) => {
    try {
      await addColor(data);
      toast("Colour added successfully");
    } catch (err) {
      toast(err.message || "Failed to add colour", "error");
      throw err; // let modal know it failed (keeps it open)
    }
  };

  const handleEdit = async (id, data) => {
    try {
      await updateColor(id, data);
      toast("Colour updated successfully");
    } catch (err) {
      toast(err.message || "Failed to update colour", "error");
      throw err;
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteColor(id);
      toast("Colour deleted", "warning");
    } catch (err) {
      toast(err.message || "Failed to delete colour", "error");
      throw err;
    }
  };

  const handleToggle = async (item) => {
    if (toggling === item.id) return;
    setToggling(item.id);
    try {
      await toggleStatus(item.id, item.status); // context does optimistic update + rollback
    } catch (err) {
      toast(err.message || "Failed to update status", "error");
    } finally {
      setToggling(null);
    }
  };

  /* ── render ── */
  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
      `}</style>

      <div className="space-y-5">

        {/* ── Header ── */}
        <div className="rounded-2xl overflow-hidden">
          <div className="px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-teal-700 flex items-center justify-center shadow-sm">
                  <Palette size={22} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Colours</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Manage your colour categories</p>
                </div>
              </div>
              <button onClick={() => setAddOpen(true)}
                 className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          >  <Plus size={15} /> Add Colour
              </button>
            </div>
          </div>
        </div>

        {/* ── Stat chips ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-800">{colors.length}</p>
          </div>
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Active</p>
            <p className="text-2xl font-bold text-teal-600">{activeCount}</p>
          </div>
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Inactive</p>
            <p className="text-2xl font-bold text-red-500">{inactiveCount}</p>
          </div>
        </div>

        {/* ── Search + Sort ── */}
    {colors.length > 0 && (
  <div className="rounded-2xl border border-[#e8e0d0] overflow-hidden bg-white">

    {/* Search row */}
    <div className="px-5 py-4 flex items-center gap-3">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search colour or hex…"
          className="w-full pl-9 pr-9 h-14 rounded-xl border border-[#e2ddd4] text-sm bg-[#fdfcfa] text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
        />

        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Filter button */}
      <button
        onClick={() => setFiltersOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-8 h-14 rounded-xl border text-sm font-medium transition-all
          ${filtersOpen
            ? "bg-teal-50 border-teal-300 text-teal-700"
            : "bg-white border-[#e2ddd4] text-gray-600 hover:border-teal-300 hover:text-teal-700"}`}
      >
        <Filter size={14} />
        Filters
         <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>

    {/* Expandable panel */}
    {filtersOpen && (
      <div className="px-5 pb-4 border-t border-[#f0ebe0] bg-[#fffcf9]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">

          {/* Status */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            >
              <option value="all">All Colours ({colors.length})</option>
              <option value="active">Active ({activeCount})</option>
              <option value="inactive">Inactive ({inactiveCount})</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            >
              <option value="">Default</option>
              <option value="name_asc">Name A–Z</option>
              <option value="name_desc">Name Z–A</option>
              <option value="active">Active First</option>
              <option value="inactive">Inactive First</option>
            </select>
          </div>

        </div>
      </div>
    )}
  </div>
)}

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">

          {/* Loading skeleton */}
          {loading && colors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 size={28} className="text-teal-400 animate-spin" />
              <p className="text-sm text-gray-400">Loading colours…</p>
            </div>

          ) : colors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-4">
                <Palette size={26} className="text-teal-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No colours yet</p>
              <p className="text-xs text-gray-400">Click "+ Add Colour" above to get started</p>
            </div>

          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Search size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No results for "<span className="font-medium">{search}</span>"</p>
            </div>

          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#f0ebe0] bg-[#fdfcfa]">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 w-16">ID</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Colour</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Swatch / Hex</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f5f0e8]">
                  {filtered.map((item, idx) => {
                    const hex     = getHex(item) || "#ccc";
                    const active  = item.status === "Active";
                    return (
                      <tr key={item.id} className="hover:bg-[#fdfcfa] transition-colors group">
                        <td className="px-5 py-4 text-sm text-gray-700">{idx + 1}</td>
                        <td className="px-5 py-4 text-sm font-medium text-gray-800">{item.color_name}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <span className="w-7 h-7 rounded-lg border border-black/10 shadow-sm flex-shrink-0"
                              style={{ backgroundColor: hex }} />
                            <span className="text-xs font-mono text-gray-500">{hex.toUpperCase()}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-3">
                            <StatusToggle
                              active={active}
                              disabled={toggling === item.id}
                              onChange={() => handleToggle(item)}
                            />
                            <button onClick={() => setEditItem(item)}
                              className="p-1.5 rounded-lg text-teal-700 transition-all active:scale-95">
                              <Edit3 size={14} />
                            </button>
                            <button onClick={() => setDelItem(item)}
                              className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {colors.length > 0 && (
            <div className="px-5 py-3 border-t border-[#f5f0e8] bg-[#fdfcfa] flex items-center justify-between">
              <span className="text-[11px] text-gray-400">
                Showing {filtered.length} of {colors.length} colours
              </span>
              {search && filtered.length < colors.length && (
                <button onClick={() => setSearch("")} className="text-[11px] text-teal-600 hover:underline">
                  Clear filter
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <AddModal    open={addOpen}    onClose={() => setAddOpen(false)}  onSave={handleAdd} />
      <EditModal   open={!!editItem} onClose={() => setEditItem(null)}  onSave={handleEdit} item={editItem} />
      <DeleteModal open={!!delItem}  onClose={() => setDelItem(null)}   onConfirm={handleDelete} item={delItem} />
      <ToastContainer toasts={toasts} />
    </>
  );
}